import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  MoreHorizontal,
  Trash2,
  Crown,
  FileText,
  CalendarClock,
  Sparkles,
} from "lucide-react";
import NewProjectDialog from "@/components/app/NewProjectDialog";

const COVER_BG = {
  forest: "bg-forest",
  copper: "bg-[#A67C52]",
  sand: "bg-[#D9C7A7]",
  sage: "bg-[#7E9A7C]",
  dusk: "bg-[#4A4A55]",
};
const COVER_TEXT = {
  forest: "text-paper",
  copper: "text-paper",
  sand: "text-ink",
  sage: "text-paper",
  dusk: "text-paper",
};

const STATUS_LABEL = {
  draft: "Borrador",
  in_progress: "En curso",
  review: "En revisión",
  ready_for_defense: "Lista para defensa",
};

export default function ProjectsView() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [meta, setMeta] = useState({ total: 0, limit: 1, can_create_more: true });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const isPremium = user?.plan === "premium";
  const locked = !isPremium && projects.length >= 1;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/projects");
      setProjects(data.projects || []);
      setMeta({
        total: data.total,
        limit: data.limit,
        can_create_more: data.can_create_more,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudieron cargar tus proyectos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onCreated = async (project) => {
    setProjects((p) => [project, ...p]);
    await Promise.all([load(), refresh()]);
  };

  const onDelete = async (projectId) => {
    try {
      await api.delete(`/projects/${projectId}`);
      toast.success("Proyecto eliminado");
      setConfirmDelete(null);
      await Promise.all([load(), refresh()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  const headerStats = useMemo(() => {
    const totalPages = projects.reduce((acc, p) => acc + (p.pages_generated || 0), 0);
    const avgProgress = projects.length
      ? Math.round(projects.reduce((a, p) => a + (p.progress_percent || 0), 0) / projects.length)
      : 0;
    return { totalPages, avgProgress };
  }, [projects]);

  return (
    <div className="px-6 lg:px-12 py-8 lg:py-10 max-w-6xl mx-auto" data-testid="projects-view">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="text-[11px] tracking-[0.22em] uppercase text-copper font-medium">
            Mis proyectos
          </p>
          <h1 className="mt-2 font-serif text-3xl sm:text-4xl text-ink leading-tight text-balance">
            Hola, <span className="italic text-forest">{user?.name?.split(" ")[0]}</span>. Continúa tu tesis.
          </h1>
          <p className="mt-2 text-[14px] text-ink-soft">
            {locked
              ? "Has llegado al límite gratis. Sube a Premium para crear más."
              : "Crea un proyecto, define tu universidad y deja que el copiloto te guíe."}
          </p>
        </div>
        <button
          data-testid="new-project-btn"
          onClick={() => setDialogOpen(true)}
          className={`group inline-flex items-center gap-2 px-5 py-3 rounded-full text-[13px] font-medium transition-all shadow-sm hover:shadow-md hover:-translate-y-[1px] ${
            locked
              ? "bg-paper-2 text-ink border border-rule hover:bg-rule"
              : "bg-forest text-paper hover:bg-forest-dark"
          }`}
        >
          <Plus size={14} />
          {locked ? "Desbloquear más proyectos" : "Nuevo proyecto"}
        </button>
      </div>

      {/* Mini stats strip */}
      <div className="mt-7 grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        <StatChip
          label="Proyectos"
          value={`${projects.length}${isPremium ? "" : ` / ${meta.limit}`}`}
          hint={isPremium ? "Sin límite" : "Plan gratis"}
          testid="chip-projects"
        />
        <StatChip
          label="Páginas IA"
          value={`${headerStats.totalPages}${isPremium ? "" : ` / 3`}`}
          hint={isPremium ? "Ilimitadas" : "Plan gratis"}
          testid="chip-pages"
        />
        <StatChip
          label="Avance promedio"
          value={`${headerStats.avgProgress}%`}
          hint="Todos los proyectos"
          testid="chip-progress"
        />
        <StatChip
          label="Plan"
          value={isPremium ? "Premium" : "Gratis"}
          hint={
            isPremium && user.plan_expires_at
              ? `Hasta ${new Date(user.plan_expires_at).toLocaleDateString("es-MX", {
                  day: "2-digit",
                  month: "short",
                })}`
              : "Mejora cuando quieras"
          }
          testid="chip-plan"
          accent={isPremium}
        />
      </div>

      {/* Grid / empty / loading */}
      <div className="mt-10">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="rounded-2xl border border-rule bg-white/60 p-6 h-56 animate-pulse"
              />
            ))}
          </div>
        ) : projects.length === 0 ? (
          <EmptyState onCreate={() => setDialogOpen(true)} />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="projects-grid">
            {projects.map((p) => (
              <ProjectCard
                key={p.project_id}
                project={p}
                onEdit={() => navigate(`/dashboard/projects/${p.project_id}`)}
                onDelete={() => setConfirmDelete(p)}
              />
            ))}
            {!locked && (
              <button
                data-testid="card-new-project"
                onClick={() => setDialogOpen(true)}
                className="rounded-2xl border-2 border-dashed border-rule hover:border-forest/40 bg-white/30 hover:bg-white/60 p-6 text-left flex flex-col gap-3 transition-all min-h-[14rem]"
              >
                <span className="grid place-items-center h-10 w-10 rounded-full bg-forest/10 text-forest">
                  <Plus size={16} />
                </span>
                <p className="font-serif text-xl text-ink">Crear otro proyecto</p>
                <p className="text-[12.5px] text-ink-soft leading-relaxed">
                  Empieza una nueva tesis o un capítulo paralelo. Cada proyecto guarda su propia configuración.
                </p>
              </button>
            )}
          </div>
        )}
      </div>

      <NewProjectDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onCreated={onCreated}
        locked={locked}
      />

      {/* Delete confirmation */}
      {confirmDelete && (
        <div
          className="fixed inset-0 z-50 grid place-items-center px-4"
          data-testid="delete-confirm"
        >
          <div
            className="absolute inset-0 bg-ink/40 backdrop-blur-sm"
            onClick={() => setConfirmDelete(null)}
          />
          <div className="relative bg-paper border border-rule rounded-2xl p-6 max-w-sm w-full">
            <p className="text-[10px] tracking-[0.22em] uppercase text-[#B6533F] font-medium">
              Eliminar proyecto
            </p>
            <h3 className="mt-2 font-serif text-xl text-ink">
              ¿Eliminar <span className="italic">{confirmDelete.title}</span>?
            </h3>
            <p className="mt-2 text-[13px] text-ink-soft">
              Esta acción no se puede deshacer. Perderás capítulos, citas y configuración.
            </p>
            <div className="mt-5 flex gap-2 justify-end">
              <button
                onClick={() => setConfirmDelete(null)}
                className="px-4 py-2 rounded-full text-[13px] text-ink-soft hover:bg-paper-2"
              >
                Cancelar
              </button>
              <button
                data-testid="confirm-delete-btn"
                onClick={() => onDelete(confirmDelete.project_id)}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-medium text-paper bg-[#B6533F] hover:bg-[#9A4332]"
              >
                <Trash2 size={13} />
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatChip({ label, value, hint, testid, accent }) {
  return (
    <div
      data-testid={testid}
      className={`rounded-xl border ${accent ? "border-forest/20 bg-white" : "border-rule bg-white/60"} px-4 py-3`}
    >
      <p className="text-[10px] tracking-[0.22em] uppercase text-copper font-medium">{label}</p>
      <p className="mt-1 font-serif text-2xl text-ink leading-none">{value}</p>
      {hint && <p className="mt-1 text-[11px] text-ink-soft">{hint}</p>}
    </div>
  );
}

function ProjectCard({ project, onEdit, onDelete }) {
  const [menu, setMenu] = useState(false);
  const cover = COVER_BG[project.cover_color] || COVER_BG.forest;
  const coverText = COVER_TEXT[project.cover_color] || COVER_TEXT.forest;
  const percent = Math.max(0, Math.min(100, project.progress_percent || 0));
  return (
    <div
      data-testid={`project-card-${project.project_id}`}
      className="group relative rounded-2xl border border-rule bg-white overflow-hidden hover:shadow-[0_20px_40px_-25px_rgba(39,59,44,0.25)] transition-all flex flex-col"
    >
      {/* Cover */}
      <div className={`${cover} ${coverText} px-5 pt-5 pb-4 relative`}>
        <div className="flex items-start justify-between gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-black/15 px-2 py-0.5 text-[10px] tracking-[0.18em] uppercase font-medium">
            {project.citation_format}
          </span>
          <div className="relative">
            <button
              data-testid={`project-menu-${project.project_id}`}
              onClick={() => setMenu((v) => !v)}
              aria-label="Opciones"
              className="h-7 w-7 grid place-items-center rounded-full hover:bg-black/15 transition-colors"
            >
              <MoreHorizontal size={14} />
            </button>
            {menu && (
              <div
                className="absolute right-0 top-9 z-10 w-44 bg-white text-ink rounded-lg border border-rule shadow-lg overflow-hidden"
                onMouseLeave={() => setMenu(false)}
              >
                <button
                  onClick={() => {
                    setMenu(false);
                    onEdit();
                  }}
                  className="w-full text-left px-3 py-2 text-[12.5px] hover:bg-paper-2 flex items-center gap-2"
                >
                  <Pencil size={12} /> Editar
                </button>
                <button
                  data-testid={`project-delete-${project.project_id}`}
                  onClick={() => {
                    setMenu(false);
                    onDelete();
                  }}
                  className="w-full text-left px-3 py-2 text-[12.5px] hover:bg-paper-2 text-[#B6533F] flex items-center gap-2"
                >
                  <Trash2 size={12} /> Eliminar
                </button>
              </div>
            )}
          </div>
        </div>
        <h3
          data-testid={`project-title-${project.project_id}`}
          className="mt-4 font-serif text-[19px] leading-tight line-clamp-3"
        >
          {project.title}
        </h3>
        <p className="mt-2 text-[11.5px] opacity-80 flex items-center gap-1.5">
          <CalendarClock size={11} />
          Actualizado{" "}
          {project.updated_at
            ? new Date(project.updated_at).toLocaleDateString("es-MX", {
                day: "2-digit",
                month: "short",
              })
            : "hoy"}
        </p>
      </div>

      {/* Body */}
      <div className="px-5 py-4 flex-1 flex flex-col gap-3">
        <div>
          <p className="text-[10px] tracking-[0.22em] uppercase text-copper font-medium">Carrera</p>
          <p
            data-testid={`project-career-${project.project_id}`}
            className="mt-0.5 text-[13.5px] text-ink"
          >
            {project.career}
          </p>
          {project.university && (
            <p className="text-[11.5px] text-ink-soft italic mt-0.5">{project.university}</p>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-[11px] text-ink-soft">Progreso</p>
            <p
              data-testid={`project-progress-${project.project_id}`}
              className="font-serif text-base text-ink leading-none"
            >
              {percent}%
            </p>
          </div>
          <div className="h-2 rounded-full bg-paper-2 overflow-hidden">
            <div
              className="h-full bg-forest transition-all"
              style={{ width: `${percent}%` }}
            />
          </div>
          <p className="mt-1.5 text-[11px] text-ink-soft">
            {STATUS_LABEL[project.status] || "Borrador"} ·{" "}
            {project.chapters_done}/{project.chapters_total} capítulos
          </p>
        </div>

        <button
          data-testid={`project-edit-${project.project_id}`}
          onClick={onEdit}
          className="mt-auto group/btn inline-flex items-center justify-center gap-2 py-2.5 rounded-full text-[13px] font-medium text-paper bg-forest hover:bg-forest-dark transition-all"
        >
          <Pencil size={13} />
          Editar
        </button>
      </div>
    </div>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div
      data-testid="empty-projects"
      className="rounded-2xl border border-rule bg-white p-10 lg:p-14 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center"
    >
      <div>
        <p className="text-[11px] tracking-[0.22em] uppercase text-copper font-medium">
          Tu primer paso
        </p>
        <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-ink leading-tight text-balance">
          Aún no tienes tesis activas. <span className="italic text-forest">Empecemos</span>.
        </h2>
        <p className="mt-3 text-[14px] text-ink-soft max-w-md leading-relaxed">
          Crea tu primer proyecto: definimos título, carrera y formato. En 60 segundos tendrás un esqueleto académico listo para escribir.
        </p>
        <button
          data-testid="empty-create-btn"
          onClick={onCreate}
          className="mt-7 inline-flex items-center gap-2 px-5 py-3 rounded-full text-[13px] font-medium text-paper bg-forest hover:bg-forest-dark transition-all"
        >
          <Plus size={14} />
          Crear mi primer proyecto
        </button>
      </div>
      <div className="rounded-xl border border-rule bg-paper-2/40 p-5 flex flex-col gap-3">
        <div className="flex items-start gap-3">
          <span className="grid place-items-center h-9 w-9 rounded-full bg-forest text-paper">
            <FileText size={15} />
          </span>
          <div>
            <p className="font-serif text-lg text-ink leading-tight">
              ¿Qué incluye un proyecto?
            </p>
            <p className="text-[12.5px] text-ink-soft">
              Estructura por capítulos, citas DOI verificadas, formato APA/Vancouver, simulador de jurado.
            </p>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <span className="grid place-items-center h-9 w-9 rounded-full bg-paper text-forest border border-rule">
            <Sparkles size={15} />
          </span>
          <div>
            <p className="font-serif text-lg text-ink leading-tight">Plan gratis</p>
            <p className="text-[12.5px] text-ink-soft">
              1 proyecto · 3 páginas con IA · plantilla universitaria estándar.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
